import Colors from '@root/gatsby-contentful-core/src/utils/colors';
import Market from '@root/core/src/models/market';
import PropTypes from '@root/vendor/prop-types';
import React, { useState } from '@root/vendor/react';
import Responsive from '@root/core/src/utils/responsive';
import Select from '@root/core/src/components/select';
import StateSVGData from '@root/joinroot.com/src/assets/data/driving-data/interactive-states';
import { P1 } from '@root/gatsby-contentful-core/src/components/text';
import { StyleSheet } from '@root/core/src/utils/styles';

const allMarkets = Object.keys(Market.MAPPING);

export default function ProductAvailabilityMap({
  setFormattedMarketData, setIsMouseOverMap, allMarketSupport, productAvailabilityContent,
}) {
  const [selectedMarket, setSelectedMarket] = useState(null);

  const marketSelectOptions = allMarkets.map((marketId) => ({
    label: Market.MAPPING[marketId],
    value: marketId,
  }));

  function handleMarketChange(marketId) {
    marketId = marketId.toUpperCase();

    const marketSupportInfo = allMarketSupport ? allMarketSupport[marketId] : null;

    const formattedData = {
      market: marketId,
      auto: marketSupportInfo?.auto || false,
      renters: marketSupportInfo?.renters || false,
      homeowners: marketSupportInfo?.homeowners || false,
    };

    setSelectedMarket(marketId);
    setFormattedMarketData(formattedData);
  }

  return (
    <div
      css={styles.interactiveMapContainer}
    >
      <svg
        css={styles.interactiveMap}
        viewBox={'0 0 980 593'}
        xmlns={'http://www.w3.org/2000/svg'}
      >
        <g
          css={styles.states}
          data-testid={'product-availability-map'}
          onMouseEnter={() => setIsMouseOverMap(true)}
          onMouseLeave={() => setIsMouseOverMap(false)}
        >
          {StateSVGData.map((market, index) => {
            const marketIdentifier = market.id.toUpperCase();
            const autoInfo = `Auto ${allMarketSupport && allMarketSupport[marketIdentifier]?.auto ? 'available' : 'unavailable'}`;
            const rentersInfo = `Renters ${allMarketSupport && allMarketSupport[marketIdentifier]?.renters ? 'available' : 'unavailable'}`;
            const homeownersInfo = `Homeowners ${allMarketSupport && allMarketSupport[marketIdentifier]?.homeowners ? 'available' : 'unavailable'}`;
            return (
              <g
                aria-describedby={`${market.id}-info`}
                aria-label={`${Market.MAPPING[marketIdentifier]}`}
                css={styles.stateGroup}
                data-state-id={market.id}
                data-testid={market.id}
                key={`state-group-${index}`}
                onMouseOver={() => handleMarketChange(market.id)}
                role={'state'}
                tabIndex={'0'}
              >
                <path
                  className={'state'}
                  css={styles.state}
                  d={market.shape.dimensions}
                />
                {market.shape.labelLine &&
                  <path
                    css={styles.labelLine}
                    d={market.shape.labelLine.dimensions}
                    transform={'translate(0, -0.4)'}
                  />
                }
                {market.id.toUpperCase() === 'DC' &&
                  <circle
                    css={styles.state}
                    cx={market.shape.circleDimensions.cx}
                    cy={market.shape.circleDimensions.cy}
                    opacity={'1'}
                    r={market.shape.circleDimensions.r}
                    stroke={`${Colors.white()}`}
                    strokeWidth={'1.5'}
                  />
                }
                <text
                  css={styles.mapText}
                  transform={`${market.shape.labelTransform}`}
                >
                  {market.id.toUpperCase()}
                </text>
                <p
                  id={`${market.id}-info`}
                >
                  {autoInfo}, {rentersInfo}, {homeownersInfo}
                </p>
              </g>
            );
          })}
        </g>
      </svg>
      <div css={styles.stateSelectContainer}>
        <P1 css={styles.stateSelectHeading}>See product availability for</P1>
        <Select
          inputId={'map-state-select'}
          inputName={'map-state-select'}
          onChange={(marketId) => handleMarketChange(marketId)}
          options={marketSelectOptions}
          placeholder={'Select a state'}
          selectedValue={selectedMarket || ''}
        />
      </div>
      <div
        css={styles.marketInfoOnMobile}
        data-testid={'product-availability-market-info-mobile'}
      >
        {selectedMarket && productAvailabilityContent}
      </div>
    </div>
  );
}

ProductAvailabilityMap.propTypes = {
  allMarketSupport: PropTypes.object,
  productAvailabilityContent: PropTypes.node.isRequired,
  setFormattedMarketData: PropTypes.func.isRequired,
  setIsMouseOverMap: PropTypes.func.isRequired,
};

const styles = StyleSheet.create({
  interactiveMapContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    flexDirection: 'column',
    width: '100%',
    maxWidth: 1400,
    ...Responsive.md({
      position: 'relative',
    }),
  },
  interactiveMap: {
    width: '100%',
    maxWidth: 940,
    height: 'auto',
    overflow: 'visible',
    cursor: 'crosshair',
  },
  stateGroup: {
    pointerEvents: 'all',
    textDecoration: 'none',
  },
  state: {
    fill: Colors.grayLight(),
  },
  mapText: {
    fill: Colors.nearBlack(),
    textAnchor: 'middle',
    fontSize: 12,
    fontWeight: 600,
    ...Responsive.md({
      fontSize: 12,
    }),
  },
  labelLine: {
    strokeWidth: 1,
    stroke: Colors.nearBlack(),
  },
  stateSelectContainer: {
    margin: '30px auto',
    width: '100%',
    maxWidth: 640,
    ...Responsive.md({
      display: 'none',
    }),
  },
  stateSelectHeading: {
    marginBottom: 15,
  },
  marketInfoOnMobile: {
    textAlign: 'left',
    ...Responsive.md({
      display: 'none',
    }),
  },
});
